// services/api/auth.js
import { API_CONFIG } from './config';
import { apiClient } from './client';

const TOKEN_KEYS = {
  ACCESS: 'accessToken',
  REFRESH: 'refreshToken',
  USERNAME: 'username',
  REMEMBER_ME: 'rememberMe',
  SESSION_EXPIRY: 'sessionExpiry'
};

const AUTH_EVENTS = {
  LOGIN: 'auth:login',
  LOGOUT: 'auth:logout',
  SESSION_EXPIRED: 'auth:session_expired'
};

export const authService = {
  _clearTokens() {
    // Clear tokens from both localStorage and sessionStorage
    Object.values(TOKEN_KEYS).forEach(key => {
      localStorage.removeItem(key);
      sessionStorage.removeItem(key);
    });
    // Dispatch logout event
    window.dispatchEvent(new CustomEvent(AUTH_EVENTS.LOGOUT));
  },

  _setTokens(tokens, username, rememberMe = false) {
    if (tokens?.access) {
      // Set the storage method based on remember me preference
      const storage = rememberMe ? localStorage : sessionStorage;
      
      // Store access token
      storage.setItem(TOKEN_KEYS.ACCESS, tokens.access);
      
      // Store refresh token if provided
      if (tokens.refresh) {
        storage.setItem(TOKEN_KEYS.REFRESH, tokens.refresh);
      }
      
      // Store username if provided
      if (username) {
        storage.setItem(TOKEN_KEYS.USERNAME, username);
      }
      
      // Store remember me preference
      localStorage.setItem(TOKEN_KEYS.REMEMBER_ME, rememberMe.toString());
      
      // Calculate and store session expiry (30 days for remember me, 1 day for session)
      const expiryDays = rememberMe ? 30 : 1;
      const expiryTime = Date.now() + (expiryDays * 24 * 60 * 60 * 1000);
      storage.setItem(TOKEN_KEYS.SESSION_EXPIRY, expiryTime.toString());
      
      // Dispatch login event
      window.dispatchEvent(new CustomEvent(AUTH_EVENTS.LOGIN, { 
        detail: { username, rememberMe } 
      }));
    }
  },

  async login(username, password, rememberMe = false) {
    try {
      const data = await apiClient.request(
        API_CONFIG.ENDPOINTS.AUTH.LOGIN,
        {
          method: 'POST',
          body: JSON.stringify({ username, password })
        }
      );

      if (!data?.access) {
        throw new Error('Invalid login response');
      }

      this._setTokens(data, username, rememberMe);
      return data;
    } catch (error) {
      this._clearTokens();
      throw error;
    }
  },

  async register(username, password) {
    try {
      const data = await apiClient.request(
        API_CONFIG.ENDPOINTS.AUTH.REGISTER,
        {
          method: 'POST',
          body: JSON.stringify({ username, password })
        }
      );

      if (data?.success) {
        // Auto-login with rememberMe=true for newly registered users
        return await this.login(username, password, true);
      }

      throw new Error(data?.error || 'Registration failed');
    } catch (error) {
      this._clearTokens();
      throw error;
    }
  },

  async logout() {
    try {
      // Check both storage locations for refresh token
      const refreshToken = sessionStorage.getItem(TOKEN_KEYS.REFRESH) ||
                          localStorage.getItem(TOKEN_KEYS.REFRESH);
      if (refreshToken) {
        await apiClient.request(
          API_CONFIG.ENDPOINTS.AUTH.LOGOUT,
          {
            method: 'POST',
            body: JSON.stringify({ refresh_token: refreshToken })
          }
        );
      }
    } finally {
      this._clearTokens();
    }
  },

  async refreshToken() {
    // Check both storage locations
    const refreshToken = sessionStorage.getItem(TOKEN_KEYS.REFRESH) ||
                        localStorage.getItem(TOKEN_KEYS.REFRESH);
    if (!refreshToken) {
      throw new Error('No refresh token available');
    }

    // Get remember me preference to maintain storage consistency
    const rememberMe = localStorage.getItem(TOKEN_KEYS.REMEMBER_ME) === 'true';
    const storage = rememberMe ? localStorage : sessionStorage;
    const username = storage.getItem(TOKEN_KEYS.USERNAME) ||
                    localStorage.getItem(TOKEN_KEYS.USERNAME);

    try {
      const response = await fetch(
        `${API_CONFIG.BASE_URL}${API_CONFIG.ENDPOINTS.AUTH.REFRESH}`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({ refresh: refreshToken })
        }
      );

      if (!response.ok) {
        throw new Error('Token refresh failed');
      }

      const data = await response.json();
      if (!data?.access) {
        throw new Error('Invalid refresh response');
      }

      // Use the new refresh token if provided (token rotation)
      this._setTokens({
        access: data.access,
        refresh: data.refresh || refreshToken
      }, username, rememberMe);

      return data.access;
    } catch (error) {
      this._clearTokens();
      throw error;
    }
  },

  checkAuth() {
    // Check both localStorage and sessionStorage for tokens
    const rememberMe = localStorage.getItem(TOKEN_KEYS.REMEMBER_ME) === 'true';
    const storage = rememberMe ? localStorage : sessionStorage;
    
    // Get tokens and user info
    const token = storage.getItem(TOKEN_KEYS.ACCESS) || localStorage.getItem(TOKEN_KEYS.ACCESS);
    const username = storage.getItem(TOKEN_KEYS.USERNAME) || localStorage.getItem(TOKEN_KEYS.USERNAME);
    const expiryStr = storage.getItem(TOKEN_KEYS.SESSION_EXPIRY) || localStorage.getItem(TOKEN_KEYS.SESSION_EXPIRY);
    
    // Check if session is expired
    if (token && expiryStr) {
      const expiry = parseInt(expiryStr, 10);
      if (Date.now() > expiry) {
        // Session expired, clear tokens and return not authenticated
        this._clearTokens();
        window.dispatchEvent(new CustomEvent(AUTH_EVENTS.SESSION_EXPIRED));
        return { isAuthenticated: false, username: null };
      }
    }
    
    return { 
      isAuthenticated: !!token,
      username,
      rememberMe
    };
  },

  onAuthChange(callback) {
    const handler = (event) => {
      const isLogin = event.type === AUTH_EVENTS.LOGIN;
      const isExpired = event.type === AUTH_EVENTS.SESSION_EXPIRED;
      callback(isLogin, isExpired);
    };
    
    window.addEventListener(AUTH_EVENTS.LOGIN, handler);
    window.addEventListener(AUTH_EVENTS.LOGOUT, handler);
    window.addEventListener(AUTH_EVENTS.SESSION_EXPIRED, handler);
    
    return () => {
      window.removeEventListener(AUTH_EVENTS.LOGIN, handler);
      window.removeEventListener(AUTH_EVENTS.LOGOUT, handler);
      window.removeEventListener(AUTH_EVENTS.SESSION_EXPIRED, handler);
    };
  }
};
